import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobalState } from '../contexts/GlobalContext';
import { apiFetch } from '../utils/api';
import Snackbar from '../Components/Snackbar';

function TestDashboard() {
  const { state } = useGlobalState();
  const navigate = useNavigate();
  const user = state?.user;

  const [isRunning, setIsRunning] = useState(false);
  const [isReminderRunning, setIsReminderRunning] = useState(false);
  const [results, setResults] = useState([]);
  const [summary, setSummary] = useState({ passed: 0, failed: 0, durationMs: 0 });
  const [snackbar, setSnackbar] = useState({ show: false, message: '', type: 'success' });

  const isTitan = useMemo(() => user && user.role === 'Titan', [user]);

  const buildSuite = () => {
    const currentUserId = state?.user?.id ?? state?.user?.userId;

    return [
      {
        name: 'Session check',
        detail: 'Ensure a user is loaded',
        run: async () => {
          if (!currentUserId) throw new Error('No user in session; log in first.');
          return `User ${currentUserId} loaded`;
        },
      },
      {
        name: 'Fetch listings',
        detail: 'GET /fetchListings returns array',
        run: async () => {
          const res = await apiFetch('fetchListings');
          const listings = res.listings || res.properties || [];
          if (!Array.isArray(listings)) throw new Error('Listings not returned as an array');
          return `Received ${listings.length} listings`;
        },
      },
      {
        name: 'Fetch properties',
        detail: 'GET /fetchProperties returns array',
        run: async () => {
          const res = await apiFetch('fetchProperties');
          const props = res.properties || [];
          if (!Array.isArray(props)) throw new Error('Properties not returned as an array');
          return `Received ${props.length} properties`;
        },
      },
      {
        name: 'Fetch contacts',
        detail: 'GET /fetchContacts returns array for current user',
        run: async () => {
          if (!currentUserId) throw new Error('No user in session; cannot fetch contacts');
          const res = await apiFetch('fetchContacts', { queryParams: { user_id: currentUserId } });
          const contacts = res.contacts || [];
          if (!Array.isArray(contacts)) throw new Error('Contacts not returned as an array');
          return `Received ${contacts.length} contacts`;
        },
      },
      {
        name: 'Fetch users',
        detail: 'GET /fetchUsers returns array',
        run: async () => {
          const res = await apiFetch('fetchUsers');
          const users = res.users || [];
          if (!Array.isArray(users)) throw new Error('Users not returned as an array');
          return `Received ${users.length} users`;
        },
      },
    ];
  };

  useEffect(() => {
    if (!isTitan) return;
    const initialSuite = buildSuite().map((test) => ({
      name: test.name,
      status: 'idle',
      detail: test.detail,
    }));
    setResults(initialSuite);
  }, [isTitan, state?.user]);

  if (!isTitan) {
    return (
      <div className="container mt-4">
        <p className="text-danger">Forbidden: Titan role required.</p>
      </div>
    );
  }

  const formatMs = (ms) => `${(ms / 1000).toFixed(2)}s`;

  const handleRun = async () => {
    const suite = buildSuite();
    setIsRunning(true);
    setSummary({ passed: 0, failed: 0, durationMs: 0 });
    setSnackbar({ show: true, message: 'Running tests…', type: 'info' });
    setResults(suite.map((t) => ({ name: t.name, status: 'running', detail: t.detail })));

    const start = performance.now();
    let passed = 0;
    let failed = 0;
    const runResults = [];

    for (const test of suite) {
      try {
        const detail = await test.run();
        passed += 1;
        runResults.push({ name: test.name, status: 'passed', detail: typeof detail === 'string' ? detail : test.detail });
      } catch (err) {
        failed += 1;
        runResults.push({ name: test.name, status: 'failed', detail: err.message || 'Unknown error' });
      }
      setResults([...runResults]);
    }

    const durationMs = performance.now() - start;
    setSummary({ passed, failed, durationMs });
    setSnackbar({ show: true, message: failed === 0 ? '✅ Tests passed' : '⚠️ Some tests failed', type: failed === 0 ? 'success' : 'warning' });
    setIsRunning(false);
  };

  const runTaskReminderCron = async () => {
    setIsReminderRunning(true);
    try {
      const res = await fetch('http://localhost:8000/api/task_reminder_cron.php', {
        method: 'POST',
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      setSnackbar({ show: true, message: '✅ Task reminder cron executed', type: 'success' });
    } catch (err) {
      setSnackbar({ show: true, message: `❌ Cron failed: ${err.message}`, type: 'danger' });
    } finally {
      setIsReminderRunning(false);
    }
  };

  return (
    <div className="container mt-4">
      <Snackbar
        show={snackbar.show}
        message={snackbar.message}
        type={snackbar.type}
        onClose={() => setSnackbar((s) => ({ ...s, show: false }))}
      />
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="mb-0">Test Dashboard</h1>
        <button className="btn btn-light btn-sm" onClick={() => navigate(-1)}>
          &larr; Back
        </button>
      </div>

      <div className="card shadow-sm mb-3">
        <div className="card-body d-flex flex-wrap gap-3 align-items-center justify-content-between">
          <div>
            <h5 className="mb-1">Suite Summary</h5>
            <div className="d-flex gap-3">
              <span className="badge bg-success">Passed: {summary.passed}</span>
              <span className="badge bg-danger">Failed: {summary.failed}</span>
              <span className="badge bg-secondary">Duration: {summary.durationMs ? formatMs(summary.durationMs) : '—'}</span>
            </div>
          </div>
          <button className="btn btn-primary" onClick={handleRun} disabled={isRunning}>
            {isRunning ? 'Running…' : 'Run Tests'}
          </button>
          <button className="btn btn-outline-primary" onClick={runTaskReminderCron} disabled={isReminderRunning}>
            {isReminderRunning ? 'Running reminders…' : 'Send Task Reminders'}
          </button>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0">Results</h5>
            {isRunning && <span className="text-muted">Executing…</span>}
          </div>
          {results.length === 0 ? (
            <p className="text-muted mb-0">No results yet.</p>
          ) : (
            <ul className="list-group list-group-flush">
              {results.map((result, idx) => (
                <li key={idx} className="list-group-item d-flex justify-content-between align-items-start">
                  <div>
                    <div className="fw-bold">{result.name}</div>
                    <small className="text-muted">{result.detail}</small>
                  </div>
                  <span
                    className={`badge bg-${
                      result.status === 'passed'
                        ? 'success'
                        : result.status === 'failed'
                        ? 'danger'
                        : result.status === 'queued' || result.status === 'running'
                        ? 'warning text-dark'
                        : 'secondary'
                    }`}
                  >
                    {result.status}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export default TestDashboard;
